import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, listingsAPI, paymentsAPI, watchlistAPI, savedSearchesAPI, verificationAPI, uploadAPI } from '../lib/api';
import { toast } from 'sonner';
import ListingCard from '../components/ListingCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Package, Settings, Zap, Clock, Eye, MousePointer, Calendar, Bell, Heart, Search, Briefcase, ShieldCheck, Trash2, ExternalLink, Upload, Loader2 } from 'lucide-react';

const CITIES = ["Pickering", "Ajax", "Whitby", "Courtice", "Oshawa", "Bowmanville"];

function timeRemaining(endDateStr) {
  const end = new Date(endDateStr);
  const now = new Date();
  const diff = end - now;
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h remaining`;
  return `${hours}h remaining`;
}

function PromotionCard({ promo }) {
  const listing = promo.listing;
  const isActive = promo.status === 'active' && new Date(promo.end_date) > new Date();
  const PLACEHOLDER_IMG = "https://images.pexels.com/photos/8135269/pexels-photo-8135269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
  const image = listing?.images?.[0] || PLACEHOLDER_IMG;
  return (
    <div className={`bg-white rounded-lg border ${isActive ? 'border-[#002FA7]/20 ring-1 ring-[#002FA7]/10' : 'border-zinc-200'} overflow-hidden`} data-testid={`promotion-card-${promo.promotion_id}`}>
      <div className="flex gap-4 p-4">
        <Link to={listing ? `/listing/${listing.listing_id}` : '#'} className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-zinc-100">
          <img src={image} alt={listing?.title || 'Listing'} className="w-full h-full object-cover" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link to={listing ? `/listing/${listing.listing_id}` : '#'} className="font-bold text-sm text-zinc-900 hover:text-[#002FA7] line-clamp-1 block">{listing?.title || 'Listing removed'}</Link>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold mt-1 ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                <Zap className="w-3 h-3" /> {isActive ? 'Active' : 'Expired'}
              </span>
            </div>
            <span className="text-xs font-mono font-semibold text-zinc-900 flex-shrink-0">${promo.amount_paid?.toFixed(2)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {promo.tier_name}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {promo.duration_days}d</span>
            {isActive && <span className="text-[#002FA7] font-semibold">{timeRemaining(promo.end_date)}</span>}
          </div>
          {listing && (
            <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {listing.promoted_views || 0} views</span>
              <span className="flex items-center gap-1"><MousePointer className="w-3 h-3" /> {listing.promoted_clicks || 0} clicks</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyDashboardPage() {
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [myListings, setMyListings] = useState([]);
  const [myPromotions, setMyPromotions] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistIds, setWatchlistIds] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promosLoading, setPromosLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', city: '', phone: '', bio: '', avatar: '' });
  const [notifPrefs, setNotifPrefs] = useState({ new_message: true, promotion_updates: true, price_drops: true });
  const [notifSaving, setNotifSaving] = useState(false);
  // Business
  const [businessForm, setBusinessForm] = useState({ company_name: '', business_description: '', website: '', business_phone: '' });
  const [businessSaving, setBusinessSaving] = useState(false);
  // Verification
  const [verStatus, setVerStatus] = useState(null);
  const [verIdType, setVerIdType] = useState('drivers_license');
  const [verDoc, setVerDoc] = useState('');
  const [verSubmitting, setVerSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    setProfileForm({ name: user.name || '', city: user.city || '', phone: user.phone || '', bio: user.bio || '', avatar: user.avatar || '' });
    if (user.account_type === 'business') {
      setBusinessForm({ company_name: user.company_name || '', business_description: user.business_description || '', website: user.website || '', business_phone: user.business_phone || '' });
    }
    listingsAPI.getMine().then(r => setMyListings(r.data)).catch(() => {}).finally(() => setLoading(false));
    paymentsAPI.myPromotions().then(r => setMyPromotions(r.data)).catch(() => {}).finally(() => setPromosLoading(false));
    watchlistAPI.getAll().then(r => setWatchlist(r.data)).catch(() => {});
    watchlistAPI.getIds().then(r => setWatchlistIds(r.data)).catch(() => {});
    savedSearchesAPI.getAll().then(r => setSavedSearches(r.data)).catch(() => {});
    usersAPI.getNotificationPrefs().then(r => setNotifPrefs(r.data)).catch(() => {});
    verificationAPI.getStatus().then(r => setVerStatus(r.data)).catch(() => {});
  }, [user, navigate, authLoading]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await usersAPI.updateProfile(profileForm);
      setUser(data);
      toast.success('Profile updated');
    } catch (err) { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleToggleNotif = async (key) => {
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    setNotifSaving(true);
    try {
      await usersAPI.updateNotificationPrefs({ [key]: updated[key] });
      toast.success('Notification preferences updated');
    } catch (err) { setNotifPrefs(notifPrefs); toast.error('Failed to update'); }
    finally { setNotifSaving(false); }
  };

  const handleRemoveWatchlist = async (listingId) => {
    try {
      await watchlistAPI.remove(listingId);
      setWatchlist(prev => prev.filter(l => l.listing_id !== listingId));
      setWatchlistIds(prev => prev.filter(id => id !== listingId));
      toast.success('Removed from watchlist');
    } catch { toast.error('Failed to remove'); }
  };

  const handleDeleteSavedSearch = async (searchId) => {
    try {
      await savedSearchesAPI.delete(searchId);
      setSavedSearches(prev => prev.filter(s => s.search_id !== searchId));
      toast.success('Search deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleBusinessUpgrade = async (e) => {
    e.preventDefault();
    if (!businessForm.company_name.trim()) { toast.error('Company name is required'); return; }
    setBusinessSaving(true);
    try {
      const { data } = await usersAPI.upgradeBusiness(businessForm);
      setUser(data);
      toast.success('Upgraded to business account!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upgrade'); }
    finally { setBusinessSaving(false); }
  };

  const handleVerificationSubmit = async () => {
    if (!verDoc) { toast.error('Please upload an ID document'); return; }
    setVerSubmitting(true);
    try {
      await verificationAPI.submit({ id_type: verIdType, document_url: verDoc });
      const { data } = await verificationAPI.getStatus();
      setVerStatus(data);
      toast.success('Verification request submitted!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setVerSubmitting(false); }
  };

  const handleVerDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await uploadAPI.uploadImage(file);
      setVerDoc(data.url);
      toast.success('Document uploaded');
    } catch { toast.error('Upload failed'); }
  };

  if (!user) return null;
  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" /></div>;

  const activePromos = myPromotions.filter(p => p.status === 'active' && new Date(p.end_date) > new Date());
  const expiredPromos = myPromotions.filter(p => p.status !== 'active' || new Date(p.end_date) <= new Date());
  const isBusiness = user.account_type === 'business';

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="dashboard-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-2">My Dashboard</h1>
        <p className="text-sm text-zinc-500 mb-8">Manage your listings, promotions, watchlist, and profile</p>

        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="bg-white border border-zinc-200 rounded-full p-1 inline-flex flex-wrap gap-1">
            <TabsTrigger value="listings" className="rounded-full px-3 py-2 text-xs sm:text-sm font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-listings">
              <Package className="w-4 h-4 mr-1" /> Listings
            </TabsTrigger>
            <TabsTrigger value="promotions" className="rounded-full px-3 py-2 text-xs sm:text-sm font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-promotions">
              <Zap className="w-4 h-4 mr-1" /> Promotions
            </TabsTrigger>
            <TabsTrigger value="watchlist" className="rounded-full px-3 py-2 text-xs sm:text-sm font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-watchlist">
              <Heart className="w-4 h-4 mr-1" /> Watchlist
              {watchlist.length > 0 && <span className="ml-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{watchlist.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="searches" className="rounded-full px-3 py-2 text-xs sm:text-sm font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-searches">
              <Search className="w-4 h-4 mr-1" /> Saved
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full px-3 py-2 text-xs sm:text-sm font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-profile">
              <Settings className="w-4 h-4 mr-1" /> Profile
            </TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-full px-3 py-2 text-xs sm:text-sm font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-1" /> Alerts
            </TabsTrigger>
          </TabsList>

          {/* LISTINGS TAB */}
          <TabsContent value="listings" data-testid="listings-tab-content">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-white rounded-lg p-4"><div className="bg-zinc-100 rounded-lg aspect-[4/3] mb-3" /><div className="h-4 bg-zinc-100 rounded w-3/4 mb-2" /><div className="h-4 bg-zinc-100 rounded w-1/2" /></div>)}
              </div>
            ) : myListings.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="my-listings-grid">
                {myListings.map(l => <ListingCard key={l.listing_id} listing={l} showActions />)}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-zinc-200 p-12 text-center">
                <Package className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-lg text-zinc-500 mb-2">No listings yet</p>
                <button onClick={() => navigate('/post')} className="bg-[#002FA7] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#002585]" data-testid="post-first-listing-btn">Post Your First Listing</button>
              </div>
            )}
          </TabsContent>

          {/* PROMOTIONS TAB */}
          <TabsContent value="promotions" data-testid="promotions-tab-content">
            {promosLoading ? <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-white rounded-lg border border-zinc-200 p-4 h-24" />)}</div>
            : myPromotions.length > 0 ? (
              <div className="space-y-6">
                {activePromos.length > 0 && <div><h2 className="text-sm font-bold text-zinc-900 mb-3">Active ({activePromos.length})</h2><div className="space-y-3" data-testid="active-promotions-list">{activePromos.map(p => <PromotionCard key={p.promotion_id} promo={p} />)}</div></div>}
                {expiredPromos.length > 0 && <div><h2 className="text-sm font-bold text-zinc-500 mb-3">Past ({expiredPromos.length})</h2><div className="space-y-3" data-testid="expired-promotions-list">{expiredPromos.map(p => <PromotionCard key={p.promotion_id} promo={p} />)}</div></div>}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-zinc-200 p-12 text-center">
                <Zap className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-lg text-zinc-500 mb-2">No promotions yet</p>
                <p className="text-sm text-zinc-400 mb-4">Promote your listings to reach more buyers</p>
                {myListings.length > 0 && <Link to={`/promote/${myListings[0].listing_id}`} className="bg-[#002FA7] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#002585] inline-flex items-center gap-2" data-testid="promote-first-listing-btn"><Zap className="w-4 h-4" /> Promote a Listing</Link>}
              </div>
            )}
          </TabsContent>

          {/* WATCHLIST TAB */}
          <TabsContent value="watchlist" data-testid="watchlist-tab-content">
            {watchlist.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="watchlist-grid">
                {watchlist.map(l => <ListingCard key={l.listing_id} listing={l} isWatchlisted onToggleWatchlist={handleRemoveWatchlist} />)}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-zinc-200 p-12 text-center">
                <Heart className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-lg text-zinc-500 mb-2">Your watchlist is empty</p>
                <p className="text-sm text-zinc-400 mb-4">Save listings you're interested in by clicking the heart icon</p>
                <Link to="/browse" className="bg-[#002FA7] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#002585]" data-testid="browse-listings-btn">Browse Listings</Link>
              </div>
            )}
          </TabsContent>

          {/* SAVED SEARCHES TAB */}
          <TabsContent value="searches" data-testid="searches-tab-content">
            {savedSearches.length > 0 ? (
              <div className="space-y-3" data-testid="saved-searches-list">
                {savedSearches.map(s => (
                  <div key={s.search_id} className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center justify-between" data-testid={`saved-search-${s.search_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#002FA7]/5 flex items-center justify-center flex-shrink-0"><Search className="w-5 h-5 text-[#002FA7]" /></div>
                      <div>
                        <p className="font-semibold text-sm text-zinc-900">{s.name}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(s.filters || {}).filter(([, v]) => v).map(([k, v]) => (
                            <span key={k} className="px-2 py-0.5 rounded-full bg-zinc-100 text-[10px] font-medium text-zinc-600 capitalize">{k}: {v}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/browse?${new URLSearchParams(s.filters).toString()}`} className="p-2 rounded-lg text-[#002FA7] hover:bg-[#002FA7]/5" data-testid={`run-search-${s.search_id}`}><ExternalLink className="w-4 h-4" /></Link>
                      <button onClick={() => handleDeleteSavedSearch(s.search_id)} className="p-2 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50" data-testid={`delete-search-${s.search_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-zinc-200 p-12 text-center">
                <Search className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-lg text-zinc-500 mb-2">No saved searches</p>
                <p className="text-sm text-zinc-400 mb-4">Save your search criteria on the Browse page to get notified about new matches</p>
                <Link to="/browse" className="bg-[#002FA7] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#002585]">Browse Listings</Link>
              </div>
            )}
          </TabsContent>

          {/* PROFILE TAB */}
          <TabsContent value="profile" data-testid="profile-tab-content">
            <div className="space-y-6 max-w-2xl">
              {/* Edit Profile */}
              <div className="bg-white rounded-lg border border-zinc-200 p-6">
                <h2 className="font-bold text-zinc-900 mb-4">Edit Profile</h2>
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Name</label><input type="text" value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7]" data-testid="profile-name-input" /></div>
                  <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">City</label><select value={profileForm.city} onChange={e => setProfileForm({ ...profileForm, city: e.target.value })} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]" data-testid="profile-city-select">{CITIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Phone</label><input type="tel" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7]" data-testid="profile-phone-input" /></div>
                  <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Bio</label><textarea value={profileForm.bio} onChange={e => setProfileForm({ ...profileForm, bio: e.target.value })} rows={3} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7] resize-none" data-testid="profile-bio-input" /></div>
                  <button type="submit" disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-6 py-2.5 font-bold text-sm disabled:opacity-50" data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save Changes'}</button>
                </form>
              </div>

              {/* Business Account */}
              <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="business-section">
                <div className="flex items-center gap-2 mb-4"><Briefcase className="w-5 h-5 text-[#002FA7]" /><h2 className="font-bold text-zinc-900">{isBusiness ? 'Business Account' : 'Upgrade to Business'}</h2></div>
                {!isBusiness && <p className="text-sm text-zinc-500 mb-4">Get a business badge, higher listing limits, and more.</p>}
                <form onSubmit={handleBusinessUpgrade} className="space-y-4">
                  <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Company Name *</label><input type="text" value={businessForm.company_name} onChange={e => setBusinessForm({ ...businessForm, company_name: e.target.value })} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7]" data-testid="business-name-input" /></div>
                  <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Description</label><textarea value={businessForm.business_description} onChange={e => setBusinessForm({ ...businessForm, business_description: e.target.value })} rows={2} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7] resize-none" data-testid="business-desc-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Website</label><input type="url" value={businessForm.website} onChange={e => setBusinessForm({ ...businessForm, website: e.target.value })} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7]" data-testid="business-website-input" /></div>
                    <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Business Phone</label><input type="tel" value={businessForm.business_phone} onChange={e => setBusinessForm({ ...businessForm, business_phone: e.target.value })} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#002FA7]" data-testid="business-phone-input" /></div>
                  </div>
                  <button type="submit" disabled={businessSaving} className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-6 py-2.5 font-bold text-sm disabled:opacity-50 flex items-center gap-2" data-testid="business-upgrade-btn">
                    {businessSaving && <Loader2 className="w-3 h-3 animate-spin" />}
                    {isBusiness ? 'Update Business Info' : 'Upgrade to Business'}
                  </button>
                </form>
              </div>

              {/* Verification */}
              <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="verification-section">
                <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-emerald-600" /><h2 className="font-bold text-zinc-900">Seller Verification</h2></div>
                {verStatus?.verified ? (
                  <div className="flex items-center gap-2 p-4 bg-emerald-50 rounded-lg"><ShieldCheck className="w-5 h-5 text-emerald-600" /><p className="text-sm font-semibold text-emerald-700">Your identity is verified</p></div>
                ) : verStatus?.request?.status === 'pending' ? (
                  <div className="p-4 bg-amber-50 rounded-lg"><p className="text-sm font-semibold text-amber-700">Verification request pending review</p><p className="text-xs text-amber-600 mt-1">Submitted on {new Date(verStatus.request.created_at).toLocaleDateString()}</p></div>
                ) : verStatus?.request?.status === 'rejected' ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 rounded-lg"><p className="text-sm font-semibold text-red-700">Previous request was rejected</p>{verStatus.request.admin_notes && <p className="text-xs text-red-600 mt-1">Reason: {verStatus.request.admin_notes}</p>}</div>
                    <VerificationForm verIdType={verIdType} setVerIdType={setVerIdType} verDoc={verDoc} handleVerDocUpload={handleVerDocUpload} handleVerificationSubmit={handleVerificationSubmit} verSubmitting={verSubmitting} />
                  </div>
                ) : (
                  <VerificationForm verIdType={verIdType} setVerIdType={setVerIdType} verDoc={verDoc} handleVerDocUpload={handleVerDocUpload} handleVerificationSubmit={handleVerificationSubmit} verSubmitting={verSubmitting} />
                )}
              </div>
            </div>
          </TabsContent>

          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications" data-testid="notifications-tab-content">
            <div className="bg-white rounded-lg border border-zinc-200 p-6 max-w-2xl">
              <div className="flex items-center gap-2 mb-6"><Bell className="w-5 h-5 text-[#002FA7]" /><div><h2 className="font-bold text-zinc-900">Email Notifications</h2><p className="text-sm text-zinc-500">Choose which emails you'd like to receive</p></div></div>
              <div className="space-y-4">
                {[
                  { key: 'new_message', label: 'New Messages', desc: 'Get notified when someone messages you about your listing' },
                  { key: 'promotion_updates', label: 'Promotion Updates', desc: 'Get notified when your promotion is activated or expires' },
                  { key: 'price_drops', label: 'Price Drop Alerts', desc: 'Get notified when a listing you were interested in drops in price' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 rounded-lg border border-zinc-200 hover:border-zinc-300 transition-colors" data-testid={`notif-toggle-${item.key}`}>
                    <div><p className="font-semibold text-sm text-zinc-900">{item.label}</p><p className="text-xs text-zinc-500">{item.desc}</p></div>
                    <button onClick={() => handleToggleNotif(item.key)} disabled={notifSaving}
                      className={`relative w-11 h-6 rounded-full transition-colors ${notifPrefs[item.key] ? 'bg-[#002FA7]' : 'bg-zinc-300'}`}
                      data-testid={`notif-switch-${item.key}`}
                    ><span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${notifPrefs[item.key] ? 'translate-x-5' : 'translate-x-0'}`} /></button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function VerificationForm({ verIdType, setVerIdType, verDoc, handleVerDocUpload, handleVerificationSubmit, verSubmitting }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">Submit an ID document to get a verified badge on your profile and listings.</p>
      <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">ID Type</label>
        <select value={verIdType} onChange={e => setVerIdType(e.target.value)} className="w-full border border-zinc-200 rounded-lg px-4 py-3 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]" data-testid="ver-id-type">
          <option value="drivers_license">Driver's License</option><option value="passport">Passport</option><option value="health_card">Health Card</option><option value="other">Other</option>
        </select>
      </div>
      <div><label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Upload Document</label>
        <div className="border-2 border-dashed border-zinc-200 rounded-lg p-6 text-center">
          {verDoc ? <div><p className="text-sm text-emerald-600 font-semibold">Document uploaded</p><img src={verDoc} alt="ID" className="max-h-32 mx-auto mt-2 rounded" /></div>
            : <div><Upload className="w-8 h-8 text-zinc-300 mx-auto mb-2" /><p className="text-sm text-zinc-500">Click to upload your ID</p></div>}
          <input type="file" accept="image/*" onChange={handleVerDocUpload} className="absolute inset-0 opacity-0 cursor-pointer" style={{ position: 'relative' }} data-testid="ver-doc-upload" />
        </div>
      </div>
      <button onClick={handleVerificationSubmit} disabled={verSubmitting} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-6 py-2.5 font-bold text-sm disabled:opacity-50 flex items-center gap-2" data-testid="ver-submit-btn">
        {verSubmitting && <Loader2 className="w-3 h-3 animate-spin" />} Submit for Verification
      </button>
    </div>
  );
}
